import MicIcon from '@mui/icons-material/Mic';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  IconButton,
  keyframes,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n/init';
import { responseHandler } from '../../responseHandler/responseHandler';
import { Theme } from '../../styles/theme/theme';
import { AlertMessage, IAlertMessage } from './components/alertMessage.component';
import { Loading } from './components/loading.component';
import { Question } from './components/question.component';
import { Response } from './components/response.component';
import {
  AIAssistanRes,
  aiAssistantService,
  AIAssistantInitialisationDataQueryRes,
} from './service/aiAssistant.service';
import { chatboxService, IChatboxMessage } from './service/chatbox.service';
import SmileFaceAi from '../../icons/smiley_ai_blue.gif';
import CloseIcon from '@mui/icons-material/Close';
import PlaceholderIcon from '../../icons/Bowtie.svg';
import InfoIcon from '@mui/icons-material/Info';
import { C_Ai_Assistant_Query_Status } from '../../../graphql/generatedModel';
import { IMenuItem, OptionalMenu } from '../optionalMenu/optionalMenu';

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: ((this: SpeechRecognition, ev: Event) => void) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => void) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => void) | null;
  onend: ((this: SpeechRecognition, ev: Event) => void) | null;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

// Declare the SpeechRecognition interface for TypeScript
declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

export const AiAssistant: FC<IAIAssistant> = ({ open, onOpen }) => {
  const { t } = useTranslation();
  const textFieldRef = useRef<HTMLInputElement>(null);
  const [messages, setMessages] = useState<IChatboxMessage[]>([]);
  const [userMessage, setUserMessage] = useState('');
  const [isListening, setIsListening] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [aiAssistant$, setAiAssistant$] = useState(aiAssistantService.sub.prompt());
  const [showingInfo, setShowingInfo] = useState<boolean>(false);
  const [infoLoading, setInfoLoading] = useState<boolean>(false);
  const [showingLowBalanceAlert, setShowingLowBalanceAlert] = useState<boolean>(false);
  const [freeCreditsBalance, setFreeCreditsBalance] = useState<number>(0);
  const [premiumCreditsBalance, setPremiumCreditsBalance] = useState<number>(0);

  // Create a ref to store the recognition instance
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const scrollTargetRef = useRef<HTMLDivElement>(null);

  const menuItems: IMenuItem[] = [
    {
      label: t('aiAssistant.information'),
      icon: <InfoIcon sx={{ fill: 'rgba(0, 0, 0, 0.54)' }} />,
      onClick: () => {
        setShowingInfo(true);
      },
    },
  ];

  const userMessageHanlder = (message: string) => {
    chatboxService.pub.message({ type: 'user', message });
    chatboxService.pub.loading(true);
    aiAssistantService.pub.prompt(message);
    setUserMessage('');
  };

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        textFieldRef.current?.querySelector('textarea')?.focus();
      }, 700);
    } else {
      textFieldRef.current?.querySelector('textarea')?.blur();
    }
  }, [open]);

  useEffect(() => {
    const unsubAIPrompt = aiAssistant$
      .pipe(
        responseHandler({
          quite: true,
          customErrorHandler: () => {
            aiAssistantService.pub.clear();
            unsubAIPrompt.unsubscribe();
            chatboxService.pub.loading(false);
            chatboxService.pub.message({
              type: 'alertMessage',
              message: {
                type: 'error',
                message: t('aiAssistant.backendError'),
              } as IAlertMessage,
            });
            setAiAssistant$(aiAssistantService.sub.prompt());
            return undefined;
          },
        }),
      )
      .subscribe((prompt) => {
        if (prompt.status === C_Ai_Assistant_Query_Status.AQS_LOADING) {
          // If pooling finished, but status it received from cache still AQS_LOADING
          // it means that timeout (5 minutes) passed without response from AI
          chatboxService.pub.message({
            type: 'alertMessage',
            message: {
              type: 'error',
              message: t('aiAssistant.backendError'),
            } as IAlertMessage,
          });
        } else {
          chatboxService.pub.message({ type: 'assistant', message: prompt });
          showWarningAboutTokensIfNeeded(prompt.reply.free_tokens, prompt.reply.premium_tokens);
        }
      });
    return () => {
      unsubAIPrompt.unsubscribe();
    };
  }, [aiAssistant$]);

  useEffect(() => {
    if (scrollTargetRef.current) {
      scrollTargetRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    const unsubMessages = chatboxService.sub.message().subscribe((messages) => {
      chatboxService.pub.loading(false);
      setMessages([...messages]);
    });

    const unsubLoading = chatboxService.sub.loading().subscribe((loading) => {
      setLoading(loading);
    });

    const unsubInfo = aiAssistantService.sub
      .info()
      .pipe(
        responseHandler<AIAssistantInitialisationDataQueryRes>({
          errorReturnType: {
            free_tokens: 0,
            premium_tokens: 0,
          },
        }),
      )
      .subscribe((data) => {
        showWarningAboutTokensIfNeeded(data.free_tokens, data.premium_tokens);
        setInfoLoading(false);
      });

    const SpeechRecognitionConstructor = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognitionConstructor) {
      const recognition = new SpeechRecognitionConstructor();

      recognition.continuous = true; // Keep listening until stopped
      recognition.interimResults = true; // Show results as they're being spoken
      recognition.lang = `${i18n.language}-${i18n.language.toLocaleUpperCase()}`; // Set language

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = 0; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }
        setUserMessage(finalTranscript + interimTranscript);
      };

      recognition.onerror = () => {
        chatboxService.pub.message({
          type: 'alertMessage',
          message: {
            type: 'info',
            message: t('aiAssistant.speechRecognitionUnavailable'),
          } as IAlertMessage,
        });
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }

    aiAssistantService.pub.info();
    setInfoLoading(true);

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      unsubLoading.unsubscribe();
      unsubMessages.unsubscribe();
      unsubInfo.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = `${i18n.language}-${i18n.language.toLocaleUpperCase()}`; // Set language
    }
  }, [i18n.language]);

  const toggleListening = () => {
    if (!recognitionRef.current) return;
    if (isListening) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
    }
    setIsListening(!isListening);
  };

  // Condition of showing warning here is taken from RMR project because it needs to be identical
  const showWarningAboutTokensIfNeeded = (freeTokens: number, premiumTokens: number) => {
    setFreeCreditsBalance(freeTokens);
    setPremiumCreditsBalance(premiumTokens);
    const freeLow = freeTokens < 100 && freeTokens > 0;
    const premiumLow = premiumTokens < 100 && premiumTokens > 0;
    setShowingLowBalanceAlert(freeLow || (premiumLow && freeTokens <= 0));
  };

  return (
    <SWrapper
      open={open}
      withLowBalanceAlert={showingLowBalanceAlert && !showingInfo}
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          e.stopPropagation();
          userMessageHanlder(userMessage);
        }
      }}
    >
      <Stack
        sx={{
          height: showingLowBalanceAlert && !showingInfo ? '144px' : '72px',
          borderBottom: `1px solid ${Theme.palette.divider}`,
        }}
        direction='column'
      >
        {showingLowBalanceAlert && !showingInfo && (
          <Stack
            sx={{
              height: '72px',
              borderBottom: `1px solid ${Theme.palette.divider}`,
              padding: '6px 16px 6px 8px',
              background: `rgba(244, 140, 7, 0.12)`,
            }}
            direction='row'
            justifyContent='start'
            alignItems='center'
          >
            <ReportProblemIcon
              sx={{
                color: 'rgba(244, 140, 7, 1)',
                marginLeft: '10px',
                marginRight: '10px',
                height: '24px',
                width: '24px',
              }}
            />
            <Stack direction='column'>
              <Typography
                width={'auto'}
                fontSize={12}
                fontWeight={600}
                color={'rgba(244, 140, 7, 1)'}
                marginBottom={'1px'}
              >
                {t('aiAssistant.freeCredits') +
                  ' ' +
                  t('aiAssistant.creditsWarning') +
                  ' (' +
                  freeCreditsBalance +
                  ').'}
              </Typography>
              <Typography
                width={'auto'}
                fontSize={12}
                fontWeight={600}
                color={'rgba(244, 140, 7, 1)'}
                marginBottom={'1px'}
              >
                {t('aiAssistant.premiumCredits') +
                  ' ' +
                  t('aiAssistant.creditsWarning') +
                  ' (' +
                  premiumCreditsBalance +
                  ').'}
              </Typography>
              <Typography
                width={'auto'}
                fontSize={12}
                fontWeight={600}
                color={'rgba(244, 140, 7, 1)'}
                marginBottom={'1px'}
                sx={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={() => setShowingInfo(true)}
              >
                {t('aiAssistant.learnMore') + '.'}
              </Typography>
            </Stack>
          </Stack>
        )}
        <Stack
          sx={{
            padding: '6px 16px 6px 8px',
          }}
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Stack direction='row' alignItems='center'>
            <img
              src={SmileFaceAi}
              alt={t('aiAssistant.title')}
              style={{
                width: '60px',
                height: '60px',
                marginRight: '8px',
              }}
            />
            <Box>
              <Typography component='div' fontSize='16px' fontWeight={600}>
                {t('aiAssistant.title')}
                <Box
                  sx={{
                    fontSize: '16px',
                    fontWeight: 300,
                    display: 'inline',
                    marginLeft: '4px',
                  }}
                >
                  {t('aiAssistant.beta')}
                </Box>
                <Tooltip
                  title={t('aiAssistant.info')}
                  slotProps={{ popper: { style: { zIndex: 99999 } } }}
                >
                  <InfoIcon
                    sx={{
                      color: Theme.palette.primary.main,
                      fontSize: '16px',
                      marginLeft: '4px',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography fontSize='12px' fontWeight={500} color={Theme.palette.text.secondary}>
                {t('aiAssistant.greeting')}
              </Typography>
            </Box>
          </Stack>
          <Stack direction='row' alignItems='center'>
            <OptionalMenu menuItems={menuItems} />
            <IconButton
              data-ph-action='ai-assistant-panel-close'
              title={t('common.close')}
              onClick={onOpen}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
      <SBox open={open}>
        <Stack direction={'column'}>
          <Box
            sx={{
              position: 'relative',
              background: Theme.palette.background.default,
            }}
          >
            <Box
              sx={{
                overflowX: 'hidden',
                overflowY: 'auto',
                height: '390px',
                padding: '16px',
              }}
            >
              {showingInfo && (
                <Box paddingBottom={'75px'}>
                  <div>
                    <Stack direction='row'>
                      <IconButton title={t('common.close')} onClick={() => setShowingInfo(false)}>
                        <ArrowBackIcon />
                      </IconButton>
                      <Typography
                        width={'auto'}
                        fontSize={14}
                        fontWeight={600}
                        color={Theme.palette.primary.main}
                        marginTop={'8px'}
                      >
                        {t('aiAssistant.information')}
                      </Typography>
                    </Stack>
                    {!infoLoading && (
                      <Box sx={{ textAlign: 'left', marginBottom: '16px', position: 'relative' }}>
                        <Box
                          sx={{
                            display: 'inline-block',
                            padding: '8px 16px 16px',
                            borderRadius: '10px',
                            background: `rgba(33, 150, 243, 0.12)`,
                            wordBreak: 'break-word',
                          }}
                        >
                          <Box
                            sx={{
                              color: Theme.palette.primary.main,
                              fontSize: '14px',
                              fontFamily: Theme.typography.fontFamily,
                              fontWeight: 600,
                              marginBottom: '6px',
                            }}
                          >
                            {t('aiAssistant.freeCredits') + ': ' + freeCreditsBalance}
                          </Box>
                          {t('aiAssistant.freeCreditsInfo')}
                        </Box>
                      </Box>
                    )}
                    {!infoLoading && (
                      <Box
                        sx={{
                          display: 'inline-block',
                          marginBottom: '16px',
                          padding: '8px 16px 16px',
                          borderRadius: '10px',
                          background: `rgba(244, 140, 7, 0.12)`,
                        }}
                      >
                        <Typography
                          width={'auto'}
                          fontSize={14}
                          fontWeight={600}
                          color={'rgba(244, 140, 7, 1)'}
                          marginBottom={'6px'}
                        >
                          {t('aiAssistant.premiumCredits') + ': ' + premiumCreditsBalance}
                        </Typography>
                        {t('aiAssistant.premiumCreditsInfo')}
                      </Box>
                    )}
                  </div>
                  {infoLoading && <Loading />}
                </Box>
              )}
              {!showingInfo && messages.length > 0 && (
                <Box paddingBottom={'75px'}>
                  {messages.map((value, idx) => {
                    return (
                      <div key={idx}>
                        {value.type === 'user' && <Question message={value.message as string} />}
                        {value.type === 'assistant' && value.message && (
                          <Box>
                            <Response data={value.message as AIAssistanRes} />
                          </Box>
                        )}
                        {value.type === 'alertMessage' && (
                          <AlertMessage
                            type={(value.message as IAlertMessage).type}
                            message={(value.message as IAlertMessage).message}
                          />
                        )}
                      </div>
                    );
                  })}
                  {loading && <Loading />}
                </Box>
              )}
              {!showingInfo && messages.length === 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <Stack direction='column' justifyContent='center' alignItems='center'>
                    <img width={80} height={89} src={PlaceholderIcon} />
                    <Typography
                      marginTop={'10px'}
                      fontSize='14px'
                      fontWeight={500}
                      color={Theme.palette.text.disabled}
                    >
                      {t('aiAssistant.placeholder')}
                    </Typography>
                  </Stack>
                </Box>
              )}

              <div ref={scrollTargetRef} />
            </Box>
          </Box>
          <Box
            sx={{
              padding: '16px 8px 16px 16px',
              position: 'relative',
              borderTop: `1px solid ${Theme.palette.divider}`,
            }}
          >
            <Stack direction='row' alignItems='center'>
              <TextField
                minRows={2}
                maxRows={2}
                multiline
                sx={{
                  marginRight: '8px',
                  width: '334px',
                  '& .MuiInputBase-root': {
                    padding: '7px 14px', // Adjust padding if needed
                    '& input': {
                      padding: '0', // Remove default input padding
                    },
                  },
                }}
                variant='outlined'
                label={t('aiAssistant.question')}
                ref={textFieldRef}
                value={userMessage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setUserMessage(e.target.value);
                }}
                slotProps={{
                  inputLabel: {
                    shrink: true, // Keeps the label in the "up" position
                  },
                }}
              />
              <Box
                sx={{
                  width: '34px',
                  height: '34px',
                  position: 'relative',
                }}
              >
                <IconButton
                  onClick={() => {
                    if (userMessage) {
                      userMessageHanlder(userMessage);
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    padding: '5px',
                    top: 0,
                    left: 0,
                    background: Theme.palette.background.default,
                    border: 'none',
                    visibility:
                      (window.SpeechRecognition || window.webkitSpeechRecognition) && !userMessage
                        ? 'hidden'
                        : 'visible',
                  }}
                >
                  <SendIcon sx={{ color: Theme.palette.primary.main }} />
                </IconButton>
                <IconButton
                  onClick={() => {
                    toggleListening();
                  }}
                  sx={{
                    padding: '5px',
                    position: 'absolute',
                    visibility:
                      (!window.SpeechRecognition && !window.webkitSpeechRecognition) ||
                      (!isListening && userMessage)
                        ? 'hidden'
                        : 'visible',
                    top: 0,
                    left: 0,
                    background: Theme.palette.background.default,
                    border: 'none',
                  }}
                >
                  {isListening ? (
                    <StopIcon
                      sx={{
                        color: Theme.palette.red.dark,
                        animation: `${scaleAnimation} 2s infinite ease-in-out`,
                      }}
                    />
                  ) : (
                    <MicIcon sx={{ color: Theme.palette.primary.main }} />
                  )}
                </IconButton>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </SBox>
    </SWrapper>
  );
};

export interface IAIAssistant {
  open: boolean;
  onOpen: () => void;
}

const scaleAnimation = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
`;

const SWrapper = styled('div')<{ open: boolean; withLowBalanceAlert: boolean }>(
  ({ theme, open, withLowBalanceAlert }) => ({
    position: 'absolute',
    width: '400px',
    bottom: '45px',
    height: withLowBalanceAlert ? '632px' : '560px',
    visibility: open ? 'visible' : 'hidden',
    right: `39px`,
    background: theme.palette.background.default,
    zIndex: 1,
    borderRadius: '10px',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0px 3px 15px 0px rgba(0, 0, 0, 0.10)',
    overflow: 'hidden',
  }),
);

const SBox = styled('div')<Pick<IAIAssistant, 'open'>>(({ open }) => ({
  position: 'relative',
  width: '100%',
  opacity: open ? 1 : 0,
  transition: 'opacity 0.3s ease',
}));
